import styled from '@emotion/styled';
import {Checkbox, FormControlLabel, FormGroup} from '@mui/material';
import React from 'react';

import {FormInputWrapper} from '@ELib/components/form/FormInputWrapper';
import {SharedProps} from '@ELib/components/form/sharedProps';
import {mixinFocus} from '@ELib/styles/mixins';

export type ECheckboxGroupItem = {
  value: string;
  label: string;
};

export type ECheckboxGroupProps = SharedProps & {
  label: string;
  items: ECheckboxGroupItem[];
  onChange: (value: string[]) => void;
  value: string[];
  alignment?: 'row' | 'column';
};

export function ECheckboxGroup({
  label,
  name,
  value,
  items,
  error,
  errorMessage,
  alignment = 'column',
  onChange,
}: ECheckboxGroupProps): React.ReactElement {
  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selectedValue = new Set(value);
    const checkboxValue = e.target.value;
    if (e.target.checked) {
      selectedValue.add(checkboxValue);
    } else {
      selectedValue.delete(checkboxValue);
    }
    onChange([...selectedValue]);
  }

  return (
    <FormInputWrapper
      className='ECheckboxGroup'
      error={error}
      errorMessage={errorMessage}
      label={label}
      labelProps={{htmlFor: name}}
    >
      <FormGroup
        data-testid={`ECheckboxGroup-${name}`}
        row={alignment === 'row'}
      >
        {items.map((item) => (
          <StyledFormControl
            key={item.value}
            control={
              <StyledCheckbox
                checked={value.includes(item.value)}
                data-testid={`Checkbox-${item.value}`}
                disableRipple
                onChange={handleChange}
              />
            }
            label={item.label}
            value={item.value}
          />
        ))}
      </FormGroup>
    </FormInputWrapper>
  );
}

const StyledFormControl = styled(FormControlLabel)`
  width: fit-content;
  & .MuiFormControlLabel-label {
    font-weight: var(--font-weight-thin);
    color: var(--color-core-black);
  }

  & .MuiCheckbox-root {
    padding-top: var(--gutter-tiny);
    padding-bottom: var(--gutter-tiny);
  }

  && .Mui-checked {
    color: var(--radio-checked-color);
  }
`;

export const StyledCheckbox = styled(Checkbox)`
  :focus-within {
    svg {
      ${mixinFocus}
    }
  }
`;
